import {
  BorderAllIcon,
  BorderBottomIcon,
  BorderLeftIcon,
  BorderRightIcon,
  BorderTopIcon,
  CanoeIcon,
  RadiusAllIcon,
  RadiusBottomLeftIcon,
  RadiusBottomRightIcon,
  RadiusTopLeftIcon,
  RadiusTopRightIcon,
} from 'canoe';
import React, { useCallback, useEffect } from 'react';

import { ColorPicker, TRANSPARENT } from '@candu-ai/craft/ui';
import { Section } from 'craft/containers/Toolbox/components';
import { getCssResetStyleMessage, StyleHierarchy } from 'craft/hooks';
import { ToolboxComponentSchema } from 'craft/types';

import { DetailedValues } from './DetailedValues';
import { useBorderCss } from './useBorderCss';

interface BorderProps {
  nodeIds: string[];
  schema: ToolboxComponentSchema['Border'];
  hasOverflow?: boolean;
  hasSectionWrapper?: boolean;
  defaultValues?: {
    borderWidth: {
      fallbackStyle: StyleHierarchy;
      fallbackValue: string;
    };
    borderColor: {
      fallbackStyle: StyleHierarchy;
      fallbackValue: string;
    };
    borderRadius: {
      fallbackStyle: StyleHierarchy;
      fallbackValue: string;
    };
  };
}

export type BorderToggle = {
  tooltip: string;
  icon: CanoeIcon;
  value: 'top' | 'right' | 'left' | 'bottom' | 'all';
};

const borderPositions: BorderToggle[] = [
  { tooltip: 'Top', icon: BorderTopIcon, value: 'top' },
  { tooltip: 'Right', icon: BorderRightIcon, value: 'right' },
  { tooltip: 'Bottom', icon: BorderBottomIcon, value: 'bottom' },
  { tooltip: 'Left', icon: BorderLeftIcon, value: 'left' },
  { tooltip: 'All', icon: BorderAllIcon, value: 'all' },
];

const radiusPositions: BorderToggle[] = [
  { tooltip: 'Top Left', icon: RadiusTopLeftIcon, value: 'top' },
  { tooltip: 'Top Right', icon: RadiusTopRightIcon, value: 'right' },
  { tooltip: 'Bottom Right', icon: RadiusBottomRightIcon, value: 'bottom' },
  { tooltip: 'Bottom Left', icon: RadiusBottomLeftIcon, value: 'left' },
  { tooltip: 'All', icon: RadiusAllIcon, value: 'all' },
];

export const Border: React.FC<BorderProps> = ({
  nodeIds,
  schema,
  hasOverflow,
  hasSectionWrapper = true,
  defaultValues,
}: BorderProps) => {
  const { cssSelectorId } = schema;
  const { css = {}, setCss, hasBorder, toggleBorder, stylesHierarchy } = useBorderCss(
    nodeIds,
    cssSelectorId,
  );

  useEffect(() => {
    // if border was previously toggled on but no values were set, reset it
    if (css.borderWidth === '0' && css.borderRadius === '0') {
      toggleBorder();
    }
  }, [nodeIds]);

  const onRadiusSet = useCallback(
    (property) => {
      // if we are setting the border-radius for a component that has an unset overflow
      // set overflow to hidden automatically
      if (property === 'borderRadius' && hasOverflow && !css.overflow) {
        setCss({ overflow: 'hidden' });
      }
    },
    [hasOverflow, css, setCss],
  );

  const Wrapper = hasSectionWrapper ? Section : React.Fragment;
  const props = hasSectionWrapper
    ? ({ isExpanded: hasBorder, onToggle: toggleBorder, name: 'Border' } as any)
    : {};

  const borderColorFallback =
    stylesHierarchy?.borderColor?.fallbackValue || defaultValues?.borderColor?.fallbackValue;

  return (
    <Wrapper {...props}>
      <DetailedValues
        nodeIds={nodeIds}
        property="borderWidth"
        toggleLabel="Position"
        inputLabel="Border Width"
        toggles={borderPositions}
        schema={schema}
        stylesHierarchy={
          stylesHierarchy?.borderWidth?.fallbackValue ? stylesHierarchy : defaultValues || {}
        }
      />

      <DetailedValues
        nodeIds={nodeIds}
        property="borderRadius"
        toggleLabel="Border Radius"
        inputLabel="Border Radius"
        toggles={radiusPositions}
        schema={schema}
        onSetCss={onRadiusSet}
        stylesHierarchy={
          stylesHierarchy?.borderRadius?.fallbackValue ? stylesHierarchy : defaultValues || {}
        }
      />
      <ColorPicker
        isInGroup={{ isLast: true }}
        label="Border Color"
        value={css?.borderColor || borderColorFallback}
        onResetTooltip={getCssResetStyleMessage(StyleHierarchy.Styleguide)}
        defaultValue={(borderColorFallback as string) || TRANSPARENT}
        onChange={(borderColor: string | undefined) => {
          if (!borderColor) {
            return setCss({ borderColor: undefined, borderStyle: undefined });
          }
          return setCss({ borderColor, borderStyle: 'solid' });
        }}
      />
    </Wrapper>
  );
};
